import React, { useState, useEffect, useRef, RefObject, useContext } from "react";
import { Outlet, NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./Layout.module.css";

import { useLogin } from "../../authConfig";

import { LoginButton } from "../../components/LoginButton";
import { IconButton } from "@fluentui/react";
import { Chat24Regular, DocumentSearch24Regular } from "@fluentui/react-icons";

import logo from "../../assets/weinig-logo.png";
import Chat from "../chat/Chat";
import { ChatContext } from "../../chatContext";

/**
 * Layout component representing the main structure of the application page.
 *
 * The Layout component includes a header with a logo/image and a hidden menu that can be toggled.
 * It uses translation for textual content and manages state to handle the visibility of a collapsible menu.
 * The component listens for clicks outside the menu to automatically close it.
 *
 * The component's header consists of a logo which links back to the homepage and contains a button
 * that toggles the display of navigation elements. It optionally includes a login button if the login feature is enabled.
 *
 * The Layout is configured with functionality to translate header texts and manage menu state, making it suitable for applications
 * requiring a navigable page header with interactive elements. The menu's visibility is managed through state, and an outside click listener
 * is applied to close the menu when a user clicks outside of it.
 */
const Layout = () => {
    const { t } = useTranslation();
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef: RefObject<HTMLDivElement> = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();
    const { clearChat } = useContext(ChatContext);

    // Check if we're on the chat page (root path)
    const isChatPage = location.pathname === "/" || location.pathname === "";

    const handleNewChat = () => {
        clearChat();
        if (!isChatPage) {
            navigate("/");
        }
    };

    const handleDocumentSearch = () => {
        navigate("/search");
    };

    const toggleMenu = () => {
        setMenuOpen(!menuOpen);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
            setMenuOpen(false);
        }
    };

    useEffect(() => {
        if (menuOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [menuOpen]);

    return (
        <div className={"h-screen max-h-screen flex flex-col"}>
            <header className={styles.header} role={"banner"}>
                <div className={styles.headerContainer} ref={menuRef}>
                    {/* Left section: Logo + Navigation Buttons grouped together */}
                    <div className={styles.headerLeftSection}>
                        <Link to="/" className={styles.headerTitleContainer}>
                            <img src={logo} alt="Header Image" className={styles.headerImage} />
                        </Link>

                        {/* Header Navigation Buttons - right next to logo */}
                        <div className={styles.headerNavButtons}>
                            <button
                                className={styles.newChatButton}
                                onClick={handleNewChat}
                                title={t("labels.newChat")}
                            >
                                <Chat24Regular className={styles.buttonIcon} />
                                <span>{t("header.newChat")}</span>
                            </button>
                            <button
                                className={styles.documentSearchButton}
                                onClick={handleDocumentSearch}
                                title={t("search.title")}
                            >
                                <DocumentSearch24Regular className={styles.buttonIcon} />
                                <span>{t("header.documentSearch")}</span>
                            </button>
                        </div>
                    </div>

                    {/*        <nav>
                        <ul className={`${styles.headerNavList} ${menuOpen ? styles.show : ""}`}>
                            <li>
                                <NavLink
                                    to="/"
                                    className={({ isActive }) => (isActive ? styles.headerNavPageLinkActive : styles.headerNavPageLink)}
                                    onClick={() => setMenuOpen(false)}
                                >
                                    {t("chat")}
                                </NavLink>
                            </li>
                            {/*                 <li>
                                <NavLink
                                    to="/qa"
                                    className={({ isActive }) => (isActive ? styles.headerNavPageLinkActive : styles.headerNavPageLink)}
                                    onClick={() => setMenuOpen(false)}
                                >
                                    {t("qa")}
                                </NavLink>
                            </li> 
                        </ul>
                    </nav> */}
                    <div className={styles.loginMenuContainer}>
                        {useLogin && <LoginButton />}
                        <IconButton
                            iconProps={{ iconName: "GlobalNavButton" }}
                            className={styles.menuToggle}
                            onClick={toggleMenu}
                            ariaLabel={t("labels.toggleMenu")}
                        />
                    </div>
                </div>
            </header>

            {/* Chat is always mounted, just hidden when not on chat page */}
            <div style={{ 
                display: isChatPage ? 'flex' : 'none', 
                flex: 1, 
                overflow: 'hidden',
                height: '100%'
            }}>
                <Chat />
            </div>
            
            {/* Other routes (search, admin, etc.) render via Outlet when not on chat page */}
            {!isChatPage && <Outlet />}
        </div>
    );
};

export default Layout;
